import React, { useEffect, useMemo, useState } from 'react';
import {
  formatLnatScore,
  formatPaperRaw,
  loadLnatPaperScores,
  loadLnatTarget,
  lnatStatusFromDbPapers,
  resetLnatSittingProgress,
  saveLnatPaperScore,
  saveLnatTarget,
  sittingScoreFromLnatPapers,
} from '../utils/lnatScore';
import useWorkspacePaperProgress, { useUserTarget } from '../hooks/useWorkspacePaperProgress';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { lnatSectionBFeedbackAccess } from '../utils/lnatSectionBFeedbackAccess';
import LnatSectionBFeedbackPanel from './LnatSectionBFeedbackPanel';
import { collectTopicPaperIds, topicAttemptRowFromMap, topicAttemptStatusRequestKey, topicRowAction } from '../utils/topicAttemptRow';
import { firstPopulatedMockTier } from '../utils/firstPopulatedMockTier';
import { useAuth } from '../../auth/hooks/useAuth';
import PracticeTopicsNav from './PracticeTopicsNav';
import PracticeTopicsPages from './PracticeTopicsPages';
import { topicPapersContent } from './TopicPapersBlock';
import {
  SUBTOPIC_PAPERS_LABEL,
  SUBTOPIC_PAPERS_ALL_PAGE,
  SUBTOPIC_TIER_PAGES,
  SUBTOPIC_PAGE_TITLES,
  subtopicPapersOfTier,
} from '../utils/subtopicPapers';
import PracticeTopicsOverview from './PracticeTopicsOverview';
import useSectionPaperTierProgress from '../hooks/useSectionPaperTierProgress';
import { PRACTICE_TOPIC_PAGE_TITLES, practiceProgressSummary } from '../utils/practiceTopics';
import './LnatWorkspace.css';

const PAGES = {
  overview: 'Overview',
  ...PRACTICE_TOPIC_PAGE_TITLES,
  'mock-all': 'Mock Tests',
  'mock-beginnings': 'Mock Tests: Beginnings',
  'mock-foundations': 'Mock Tests: Foundations',
  'mock-beyond': 'Mock Tests: Beyond',
  past: 'Past Papers',
};

const TARGET_OPTIONS = [
  { value: 28, label: 'University of Oxford — BA Law (Jurisprudence)', sectionB: 'High Weight (Shortlisting)' },
  { value: 28, label: 'University of Cambridge — BA Law (Tripos)', sectionB: 'High Weight' },
  { value: 27, label: 'London School of Economics (LSE) — LLB Law', sectionB: 'Medium/High Weight' },
  { value: 27, label: 'University College London (UCL) — LLB Law', sectionB: 'High Weight' },
  { value: 27, label: "King's College London (KCL) — LLB Law", sectionB: 'High Weight (Selection)' },
  { value: 26, label: 'Durham University — LLB Law', sectionB: 'Medium Weight' },
  { value: 26, label: 'University of Bristol — LLB Law', sectionB: 'Medium Weight' },
  { value: 25, label: 'University of Glasgow — LLB Law', sectionB: 'Holistically Evaluated' },
  { value: 25, label: 'University of Oxford — Law with foundation year', sectionB: 'High Weight (Shortlisting)' },
  { value: 24, label: 'SOAS University of London — LLB Law', sectionB: 'Holistically Evaluated' },
];

function normaliseSitting(item) {
  if (item?.pearsonSitting) return item;
  // A part on its own: Section B is position 2 of its set.
  const isB = Number(item?.position) === 2;
  return {
    ...item,
    paper1: isB ? null : item,
    paper2: isB ? item : null,
    title: item.display_name || item.title || item.id,
    startId: item.id,
    startCatalogId: item.catalog_id || item.id,
    questionCount: item.question_count || 0,
  };
}

function paperIds(sitting) {
  return [sitting?.paper1?.id, sitting?.paper2?.id].filter(Boolean);
}

function sittingLabel(sitting, index, kind) {
  if (kind === 'past') {
    const year = String(sitting?.title || '').match(/\b(20\d{2})\b/);
    return year ? year[1] : sitting?.title || `Paper ${index + 1}`;
  }
  return `Test ${index + 1}`;
}

function StatusPill({ status }) {
  const map = {
    done: ['completed', 'Completed'],
    progress: ['in-progress', 'In progress'],
    none: ['not-started', 'Not started'],
  };
  const [cls, label] = map[status] || map.none;
  return <span className={`lnat-ws-pill ${cls}`}>{label}</span>;
}

function ModeCell({ mode, status }) {
  if (status === 'none' || !mode) return '—';
  return <span className="lnat-ws-mode">{mode === 'timed' ? 'Timed' : 'Untimed'}</span>;
}

function avgScores(scores) {
  const nums = scores.filter((g) => g != null && Number.isFinite(Number(g))).map(Number);
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

function enrichSittings(sittings, scoresMap, dbPapers = {}) {
  return (sittings || []).map((raw, index) => {
    const sitting = normaliseSitting(raw);
    const aId = sitting.paper1?.id;
    const bId = sitting.paper2?.id;
    const scored = sittingScoreFromLnatPapers(aId, bId, scoresMap);
    let status = lnatStatusFromDbPapers([aId, bId].filter(Boolean), dbPapers);
    if (status === 'none') {
      status = scored.status;
      if (status === 'none' && sitting.completed) status = 'done';
    }
    return { ...sitting, index, scored, status };
  });
}

function tierStats(rows) {
  const done = rows.filter((r) => r.status === 'done').length;
  const scores = rows.map((r) => r.scored.score);
  return {
    done,
    total: rows.length,
    avg: avgScores(scores),
    pct: rows.length ? Math.round((done / rows.length) * 100) : 0,
  };
}

const LnatWorkspace = ({
  onBack,
  mockSittings = [],
  pastSittings = [],
  sectionPapers = [],
  subtopicPapers = [],
  topicSections = [],
  isPartUnlocked,
  onStartSetPaper,
  onStartTopicPaper,
  topicStatus = 'ready',
  onContinueSitting,
  onReviewSitting,
  onRequestUpgrade,
}) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [page, setPage] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scoreTick, setScoreTick] = useState(0);
  const [sectionBAvailability, setSectionBAvailability] = useState({});
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [toast, setToast] = useState(null);
  const [target, setTarget] = useUserTarget(loadLnatTarget, saveLnatTarget, userId);

  useEffect(() => {
    document.body.classList.add('exam-workspace-open');
    return () => document.body.classList.remove('exam-workspace-open');
  }, []);

  const statusRequestKey = topicAttemptStatusRequestKey([
    ...[...mockSittings, ...pastSittings].flatMap((s) => [s?.paper1?.id, s?.paper2?.id].filter(Boolean)),
    ...collectTopicPaperIds(topicSections),
  ]);

  const {
    dbPapers,
    statusState,
    scoresMap,
    completedList,
  } = useWorkspacePaperProgress('LNAT', statusRequestKey, {
    userId,
    loadScores: loadLnatPaperScores,
    saveScore: saveLnatPaperScore,
    cacheTick: scoreTick,
  });


  // Which Section B papers have essay feedback, asked about the paper rather
  // than the sitting, which may not be the one holding the essay -- same
  // reason as TARA's equivalent fetch. Asked once the statuses have loaded;
  // failing here does not disturb them.
  useEffect(() => {
    let cancelled = false;
    if (statusState !== 'ready') {
      setSectionBAvailability({});
      return undefined;
    }
    (async () => {
      try {
        const avail = await quizGeneratorAPI.getLnatSectionBFeedbackAvailability(statusRequestKey.split('\n'));
        if (!cancelled && avail?.success) setSectionBAvailability(avail.papers || {});
      } catch (availErr) {
        console.warn('LNAT Section B feedback availability fetch failed', availErr);
      }
    })();
    return () => { cancelled = true; };
  }, [statusState, statusRequestKey]);


  const beginnings = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.paper1?.tier || s.paper2?.tier) === 'beginnings'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );
  const foundations = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.paper1?.tier || s.paper2?.tier) === 'foundations'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );
  const beyond = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.paper1?.tier || s.paper2?.tier) === 'beyond'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );

  const mocksGoto = useMemo(
    () => firstPopulatedMockTier(beginnings, foundations, beyond),
    [beginnings, foundations, beyond]
  );
  const past = useMemo(
    () => enrichSittings(pastSittings, scoresMap, dbPapers),
    [pastSittings, scoresMap, dbPapers]
  );
  const topicCount = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce((n, t) => n + (t.params?.length || 0), 0),
      0
    ),
    [topicSections]
  );
  const topicsDone = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce(
        (n, t) => n + (t.params || []).filter((p) => completedList.includes(p.paper_id)).length,
        0
      ),
      0
    ),
    [topicSections, completedList]
  );

  // Section papers are listed under Practice Topics (Foundations and Beyond), so the
  // Overview's Practice Topics rows count them with the topics.
  const sectionProgress = useSectionPaperTierProgress('LNAT', sectionPapers, completedList);
  const practiceTopicsRow = practiceProgressSummary({ topicsDone, topicCount, papers: sectionProgress.all });

  const overview = useMemo(() => {
    const allMocks = [...beginnings, ...foundations, ...beyond];
    const all = [...allMocks, ...past];
    const graded = all.filter((r) => r.scored.score != null);
    const currentAvg = avgScores(graded.map((r) => r.scored.score));

    let totalQuestions = 0;
    let attempted = 0;
    let correct = 0;
    all.forEach((row) => {
      totalQuestions += row.questionCount || 0;
      // Section A only: Section B is an essay and has no correct/total.
      const a = row.scored.a;
      if (!a) return;
      const answered = Number(a.answered);
      attempted += Number.isFinite(answered) ? answered : (Number(a.total) || 0);
      correct += Number(a.correct) || 0;
    });

    const timed = graded.filter((r) => r.scored.mode === 'timed');
    const untimed = graded.filter((r) => r.scored.mode === 'untimed');
    const mocksDone = allMocks.filter((r) => r.status === 'done').length;
    const pastDone = past.filter((r) => r.status === 'done').length;

    return {
      currentAvg,
      totalQuestions,
      attempted,
      successRate: attempted > 0 ? Math.round((correct / attempted) * 100) : null,
      scoredAttempts: graded.length,
      beginnings: tierStats(beginnings),
      foundations: tierStats(foundations),
      beyond: tierStats(beyond),
      mocks: {
        done: mocksDone,
        total: allMocks.length,
        avg: avgScores(allMocks.map((r) => r.scored.score)),
        pct: allMocks.length ? Math.round((mocksDone / allMocks.length) * 100) : 0,
      },
      past: {
        done: pastDone,
        total: past.length,
        avg: avgScores(past.map((r) => r.scored.score)),
        pct: past.length ? Math.round((pastDone / past.length) * 100) : 0,
      },
      timed: {
        count: timed.length,
        total: graded.length,
        avg: avgScores(timed.map((r) => r.scored.score)),
        pct: graded.length ? Math.round((timed.length / graded.length) * 100) : 0,
      },
      untimed: {
        count: untimed.length,
        total: graded.length,
        avg: avgScores(untimed.map((r) => r.scored.score)),
        pct: graded.length ? Math.round((untimed.length / graded.length) * 100) : 0,
      },
    };
  }, [beginnings, foundations, beyond, past]);

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 2600);
  };

  const goTo = (id) => {
    setPage(id);
    setMobileOpen(false);
  };

  const startSitting = (sitting, options = {}) => {
    const paper = sitting.paper1 || sitting.paper2;
    if (!paper) return;
    const unlocked = isPartUnlocked ? isPartUnlocked(paper) : true;
    if (!unlocked) {
      if (onRequestUpgrade) onRequestUpgrade(paper);
      return;
    }
    onStartSetPaper(
      'LNAT',
      sitting.startId,
      sitting.startCatalogId,
      sitting.paper2
        ? { id: sitting.paper2.id, catalog_id: sitting.paper2.catalog_id || sitting.paper2.id }
        : null,
      options
    );
  };

  const handleRetake = (sitting) => {
    resetLnatSittingProgress(userId, paperIds(sitting));
    setScoreTick((n) => n + 1);
    startSitting(sitting, { forceNew: true });
  };

  // Same reason as TaraWorkspace's openWritingFeedback: the sitting's own
  // score is a fallback for the ordinary case, but the server's availability
  // answer is what actually knows which attempt holds the essay.
  const openSectionBFeedback = (sitting) => {
    const sectionBPaperId = sitting?.paper2?.id;
    const access = lnatSectionBFeedbackAccess({
      sectionBPaperId,
      dbPapers,
      availability: sectionBAvailability,
      scoredSectionB: sitting?.scored?.b,
    });
    if (!access.available) {
      showToast('That essay has no saved attempt to look at yet.');
      return;
    }
    setFeedbackFor({
      attemptId: access.attemptId,
      paperId: sectionBPaperId,
      paper: sitting.paper2,
      // Feedback follows the essay's own paper: open when that paper is.
      // No paper is refused by the server, so it shows locked too.
      unlocked: Boolean(sitting.paper2) && (isPartUnlocked ? isPartUnlocked(sitting.paper2) : true),
      fromEarlierSitting: access.fromEarlierSitting,
      label: sitting?.title || sittingLabel(sitting, sitting?.index || 0, 'mock'),
    });
    setPage('section-b-feedback');
  };

  const handleContinue = (sitting) => {
    if (typeof onContinueSitting === 'function' && sitting) {
      onContinueSitting(sitting);
      return;
    }
    showToast('Resume isn’t available yet — use Start or Retake for a new attempt.');
  };

  const handleReview = (sitting) => {
    if (typeof onReviewSitting === 'function' && sitting) {
      onReviewSitting(sitting);
      return;
    }
    showToast('Review isn’t available yet.');
  };

  const targetValue = Number(target?.value) || 0;
  const avg = overview.currentAvg;
  const fillPct = avg != null ? Math.min(100, (avg / 42) * 100) : 0;
  const markerPct = Math.min(100, (targetValue / 42) * 100);
  const diff = avg != null ? Math.round((avg - targetValue) * 10) / 10 : null;
  const markerLabel = (target?.label || 'Target').split(' — ')[0] || 'Target';

  const selectValue = target?.custom
    ? 'custom'
    : TARGET_OPTIONS.find((o) => o.label === target?.label)?.label || TARGET_OPTIONS[2].label;
  const selectedTrack = TARGET_OPTIONS.find((o) => o.label === selectValue);
  const selectDisplay = selectValue === 'custom'
    ? 'Custom target…'
    : selectedTrack
      ? `${selectedTrack.label} (${selectedTrack.value}+/42)`
      : selectValue;

  const onTargetChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      const next = { value: Number(target.value) || 27, label: 'Custom', custom: true };
      setTarget(next);
      return;
    }
    const opt = TARGET_OPTIONS.find((o) => o.label === val);
    if (!opt) return;
    const next = { value: opt.value, label: opt.label, sectionB: opt.sectionB, custom: false };
    setTarget(next);
  };

  const onCustomInput = (e) => {
    const value = Number(e.target.value) || 0;
    const next = { value, label: 'Custom', custom: true };
    setTarget(next);
  };

  const renderTopicsTable = (params) => {
    if (!params?.length) return null;
    return (
      <table className="lnat-ws-table">
        <thead>
          <tr>
            <th>Topic</th>
            <th>Questions</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {params.map((param) => {
            const done = completedList.includes(param.paper_id);
            const action = topicRowAction(topicAttemptRowFromMap(dbPapers, param.paper_id), done);
            const status = action.status;
            const title = param.title || param.paper_id;
            return (
              <tr key={param.paper_id}>
                <td data-label="Topic" className="lnat-ws-cell-strong">{title}</td>
                <td data-label="Questions" className="lnat-ws-cell-mono">
                  {param.question_count != null ? param.question_count : '—'}
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  <button
                    type="button"
                    className={action.resume ? 'lnat-ws-start-btn mid' : 'lnat-ws-start-btn'}
                    onClick={() => onStartTopicPaper?.('LNAT', param, {
                      forceNew: action.forceNew,
                      resume: action.resume,
                    })}
                  >
                    {action.label}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderTable = (rows, kind) => (
    <table className="lnat-ws-table">
      <thead>
        <tr>
          <th>{kind === 'past' ? 'Paper' : 'Test'}</th>
          <th>Section A</th>
          <th>Section B</th>
          <th>Score</th>
          <th>Mode</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const paper = row.paper1 || row.paper2;
          const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
          const { scored, status } = row;
          return (
            <tr key={row.startId || row.title}>
              <td data-label={kind === 'past' ? 'Paper' : 'Test'} className="lnat-ws-cell-strong">
                {sittingLabel(row, row.index, kind)}
                {!unlocked && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--lnat-ink-faint)' }}>Locked</span>
                )}
              </td>
              <td data-label="Section A" className="lnat-ws-cell-mono">
                {formatPaperRaw(scored.a?.correct, scored.a?.total)}
              </td>
              <td data-label="Section B" className="lnat-ws-cell-mono">
                {scored.b ? 'Submitted' : '—'}
              </td>
              <td data-label="Score" className="lnat-ws-cell-mono">
                {formatLnatScore(scored.score)}
              </td>
              <td data-label="Mode">
                <ModeCell mode={scored.mode} status={status} />
              </td>
              <td data-label="Status">
                <StatusPill status={status} />
              </td>
              <td data-label="">
                {status === 'done' ? (
                  <div className="lnat-ws-actions">
                    {row.paper2 ? (
                      <button
                        type="button"
                        className="lnat-ws-start-btn ghost"
                        onClick={() => (unlocked ? openSectionBFeedback(row) : onRequestUpgrade?.(paper))}
                      >
                        See Feedback
                      </button>
                    ) : null}
                    <button type="button" className="lnat-ws-start-btn ghost" onClick={() => handleReview(row)}>
                      Review
                    </button>
                    <button
                      type="button"
                      className="lnat-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : status === 'progress' ? (
                  <div className="lnat-ws-actions">
                    <button type="button" className="lnat-ws-start-btn mid" onClick={() => handleContinue(row)}>
                      Continue
                    </button>
                    <button
                      type="button"
                      className="lnat-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="lnat-ws-start-btn"
                    onClick={() => {
                      if (!unlocked) {
                        onRequestUpgrade?.(paper);
                        return;
                      }
                      startSitting(row);
                    }}
                  >
                    Start
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const BreakdownRow = ({ name, meta, score, pct, goto, staticRow }) => {
    const inner = (
      <>
        <div className="lnat-ws-breakdown-name">{name}</div>
        <div className="lnat-ws-breakdown-bar-wrap">
          <div className="lnat-ws-breakdown-bar">
            <div className="lnat-ws-breakdown-bar-fill" style={{ width: `${pct || 0}%` }} />
          </div>
          <div className="lnat-ws-breakdown-meta">{meta}</div>
        </div>
        <div className="lnat-ws-breakdown-score">{score}</div>
        {!staticRow && <div className="lnat-ws-breakdown-arrow">›</div>}
      </>
    );
    if (staticRow) {
      return <div className="lnat-ws-breakdown-row static">{inner}</div>;
    }
    return (
      <button type="button" className="lnat-ws-breakdown-row" onClick={() => goTo(goto)}>
        {inner}
      </button>
    );
  };

  return (
    <div className="lnat-ws">
      <aside className={`lnat-ws-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="lnat-ws-sidebar-head">
          <span className="lnat-ws-name">LNAT Workspace</span>
          <button
            type="button"
            className="lnat-ws-icon-btn"
            title="Collapse sidebar"
            onClick={() => (mobileOpen ? setMobileOpen(false) : setCollapsed((c) => !c))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
              <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <nav className="lnat-ws-nav">
          <button
            type="button"
            className={`lnat-ws-nav-item ${page === 'overview' ? 'active' : ''}`}
            onClick={() => goTo('overview')}
          >
            <svg className="lnat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="lnat-ws-nav-label">Overview</span>
          </button>

          <PracticeTopicsNav prefix="lnat-ws" page={page} onGo={goTo} />
          <PracticeTopicsNav
            prefix="lnat-ws"
            page={page}
            onGo={goTo}
            label={SUBTOPIC_PAPERS_LABEL}
            pages={SUBTOPIC_TIER_PAGES}
          />

          <div className="lnat-ws-nav-group">
            <div className="lnat-ws-nav-item lnat-ws-nav-parent">
              <svg className="lnat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <span className="lnat-ws-nav-label">Mock Tests</span>
            </div>
            <div className="lnat-ws-nav-sub">
              {[
                ['mock-beginnings', 'Beginnings', 1],
                ['mock-foundations', 'Foundations', 2],
                ['mock-beyond', 'Beyond', 3],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`lnat-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="lnat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="lnat-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {[
            ['mock-beginnings', 'Beginnings', 1],
            ['mock-foundations', 'Foundations', 2],
            ['mock-beyond', 'Beyond', 3],
          ].map(([id, label, tier]) => (
            <button
              key={`c-${id}`}
              type="button"
              className={`lnat-ws-nav-item lnat-ws-collapsed-tier ${page === id ? 'active' : ''}`}
              title={label}
              onClick={() => goTo(id)}
            >
              <svg className="lnat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
              </svg>
              <span className="lnat-ws-nav-label">{label}</span>
            </button>
          ))}

          <button
            type="button"
            className={`lnat-ws-nav-item ${page === 'past' ? 'active' : ''}`}
            onClick={() => goTo('past')}
          >
            <svg className="lnat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <path d="M4 19.5V6a2 2 0 012-2h10.5a1.5 1.5 0 011.5 1.5V19a1 1 0 01-1 1H6.5A2.5 2.5 0 014 19.5z" stroke="currentColor" strokeWidth="1.6" />
              <line x1="8" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="lnat-ws-nav-label">Past Papers</span>
          </button>
        </nav>
      </aside>

      <main className="lnat-ws-main">
        <div className="lnat-ws-topbar-mobile">
          <button type="button" className="lnat-ws-icon-btn" onClick={() => setMobileOpen((o) => !o)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <span>{PAGES[page]}</span>
        </div>

        <div className="lnat-ws-back-row">
          <button type="button" className="lnat-ws-icon-btn lnat-ws-back" title="Back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {page === 'overview' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Overview</h1>
            </div>

            <div className="lnat-ws-target-card">
              <div className="lnat-ws-target-field lnat-ws-target-field--university">
                <label htmlFor="lnat-track">Target university</label>
                <div className="lnat-ws-target-select-shell">
                  <span className="lnat-ws-target-select-text" aria-hidden="true">
                    {selectDisplay}
                  </span>
                  <select
                    id="lnat-track"
                    className="lnat-ws-target-select"
                    value={selectValue}
                    onChange={onTargetChange}
                  >
                    {TARGET_OPTIONS.map((o) => (
                      <option key={o.label} value={o.label}>
                        {o.label} ({o.value}+/42)
                      </option>
                    ))}
                    <option value="custom">Custom target…</option>
                  </select>
                </div>
              </div>
              <div className="lnat-ws-target-field">
                <label htmlFor="lnat-custom">Custom score</label>
                <input
                  id="lnat-custom"
                  type="number"
                  className="lnat-ws-target-input"
                  min={0}
                  max={42}
                  step={1}
                  placeholder="/ 42"
                  hidden={!target.custom}
                  value={target.custom ? target.value : ''}
                  onChange={onCustomInput}
                />
              </div>
              <div className="lnat-ws-target-readout">
                <span className="num">
                  {targetValue}
                  <span style={{ color: 'var(--lnat-ink-faint)', fontWeight: 500 }}>/42</span>
                </span>
                <div className="lbl">LNAT Section A target</div>
                {!target.custom && (
                  <div style={{ color: 'var(--lnat-ink-faint)', fontSize: 11 }}>
                    Section B: {target.sectionB}
                  </div>
                )}
              </div>
            </div>

            <div className="lnat-ws-section">
              <div className="lnat-ws-progress-status" style={{ marginBottom: 8 }}>
                {avg == null ? (
                  'No scored sittings yet — complete a mock or past paper to see progress.'
                ) : diff >= 0 ? (
                  <>
                    <b>{diff.toFixed(1)} points</b> above target — keep it up
                  </>
                ) : (
                  <>
                    <b>{Math.abs(diff).toFixed(1)} points</b> below target
                  </>
                )}
              </div>
              <div className="lnat-ws-progress-track">
                <div className="lnat-ws-progress-fill" style={{ width: `${fillPct}%` }} />
                <div
                  className="lnat-ws-progress-marker"
                  data-label={markerLabel}
                  style={{ left: `${markerPct}%` }}
                />
              </div>
              <div className="lnat-ws-breakdown-meta">
                Current average:{' '}
                <b style={{ color: 'var(--lnat-ink)', fontFamily: 'var(--lnat-mono)' }}>
                  {formatLnatScore(avg)}
                </b>
                {overview.scoredAttempts > 0
                  ? ` · based on ${overview.scoredAttempts} scored sitting${overview.scoredAttempts === 1 ? '' : 's'}`
                  : ''}
              </div>
            </div>

            <div className="lnat-ws-stat-strip">
              <div className="lnat-ws-stat-cell">
                <span className="lnat-ws-stat-value">{overview.totalQuestions.toLocaleString()}</span>
                <span className="lnat-ws-stat-label">Total questions</span>
              </div>
              <div className="lnat-ws-stat-cell">
                <span className="lnat-ws-stat-value">{overview.attempted.toLocaleString()}</span>
                <span className="lnat-ws-stat-label">Questions attempted</span>
              </div>
              <div className="lnat-ws-stat-cell">
                <span className="lnat-ws-stat-value">
                  {overview.successRate != null ? `${overview.successRate}%` : '—'}
                </span>
                <span className="lnat-ws-stat-label">Success rate</span>
              </div>
              <div className="lnat-ws-stat-cell">
                <span className="lnat-ws-stat-value">{formatLnatScore(avg)}</span>
                <span className="lnat-ws-stat-label">Average score</span>
              </div>
            </div>

            <div className="lnat-ws-section">
              <h2 className="lnat-ws-section-title">By tier</h2>
              <BreakdownRow
                name="Beginnings"
                meta={`${overview.beginnings.done} of ${overview.beginnings.total} mocks completed`}
                score={formatLnatScore(overview.beginnings.avg)}
                pct={overview.beginnings.pct}
                goto="mock-beginnings"
              />
              <BreakdownRow
                name="Foundations"
                meta={`${overview.foundations.done} of ${overview.foundations.total} mocks completed`}
                score={formatLnatScore(overview.foundations.avg)}
                pct={overview.foundations.pct}
                goto="mock-foundations"
              />
              <BreakdownRow
                name="Beyond"
                meta={`${overview.beyond.done} of ${overview.beyond.total} mocks completed`}
                score={formatLnatScore(overview.beyond.avg)}
                pct={overview.beyond.pct}
                goto="mock-beyond"
              />
            </div>

            <div className="lnat-ws-section">
              <h2 className="lnat-ws-section-title">By type</h2>
              <BreakdownRow
                name="Practice Topics"
                meta={practiceTopicsRow.meta}
                score={practiceTopicsRow.score}
                pct={practiceTopicsRow.pct}
                goto="practice"
              />
              <BreakdownRow
                name="Mock Tests"
                meta={`${overview.mocks.done} of ${overview.mocks.total} mocks completed`}
                score={formatLnatScore(overview.mocks.avg)}
                pct={overview.mocks.pct}
                goto="mock-all"
              />
              <BreakdownRow
                name="Past Papers"
                meta={`${overview.past.done} of ${overview.past.total} papers completed`}
                score={formatLnatScore(overview.past.avg)}
                pct={overview.past.pct}
                goto="past"
              />
            </div>

            <PracticeTopicsOverview
              prefix="lnat-ws"
              topicsDone={topicsDone}
              topicCount={topicCount}
              papers={sectionProgress}
              onGo={goTo}
            />

            <div className="lnat-ws-section">
              <h2 className="lnat-ws-section-title">By mode</h2>
              <BreakdownRow
                name="Timed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.timed.count} of ${overview.timed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatLnatScore(overview.timed.avg)}
                pct={overview.timed.pct}
                staticRow
              />
              <BreakdownRow
                name="Untimed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.untimed.count} of ${overview.untimed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatLnatScore(overview.untimed.avg)}
                pct={overview.untimed.pct}
                staticRow
              />
            </div>
          </section>
        )}

        <PracticeTopicsPages
          page={page}
          prefix="lnat-ws"
          exam="LNAT"
          topics={(tier) => (
            topicPapersContent({
              prefix: "lnat-ws",
              tier,
              topicSections,
              topicStatus,
              renderTable: renderTopicsTable,
            })
          )}
          sectionPapers={sectionPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />
        <PracticeTopicsPages
          page={page}
          prefix="lnat-ws"
          exam="LNAT"
          label={SUBTOPIC_PAPERS_LABEL}
          allPage={SUBTOPIC_PAPERS_ALL_PAGE}
          tierPages={SUBTOPIC_TIER_PAGES}
          titles={SUBTOPIC_PAGE_TITLES}
          ofTier={subtopicPapersOfTier}
          papers={subtopicPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />

        {page === 'mock-all' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Mock Tests</h1>
            </div>
            {[
              ['Beginnings', beginnings],
              ['Foundations', foundations],
              ['Beyond', beyond],
            ].filter(([, rows]) => rows.length).map(([label, rows]) => (
              <div key={label} className="lnat-ws-section">
                <h2 className="lnat-ws-section-title">{label}</h2>
                {renderTable(rows, 'mock')}
              </div>
            ))}
          </section>
        )}

        {page === 'mock-beginnings' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Mock Tests: Beginnings</h1>
            </div>
            {renderTable(beginnings, 'mock')}
          </section>
        )}

        {page === 'mock-foundations' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Mock Tests: Foundations</h1>
            </div>
            {renderTable(foundations, 'mock')}
          </section>
        )}

        {page === 'mock-beyond' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Mock Tests: Beyond</h1>
            </div>
            {renderTable(beyond, 'mock')}
          </section>
        )}

        {page === 'past' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Past Papers</h1>
            </div>
            {renderTable(past, 'past')}
          </section>
        )}

        {page === 'section-b-feedback' && (
          <section className="lnat-ws-page">
            <div className="lnat-ws-page-header">
              <h1 className="lnat-ws-page-title">Section B Essay Feedback</h1>
              {feedbackFor ? (
                <p className="lnat-ws-page-sub">{feedbackFor.label}</p>
              ) : null}
            </div>
            {feedbackFor ? (
              <LnatSectionBFeedbackPanel
                attemptId={feedbackFor.attemptId}
                onToast={showToast}
                unlocked={feedbackFor.unlocked}
                onRequestUpgrade={() => onRequestUpgrade?.(feedbackFor.paper)}
              />
            ) : (
              <div className="lnat-ws-empty">
                <p>Open a completed sitting and select &quot;See Feedback&quot; on its Section B essay.</p>
              </div>
            )}
          </section>
        )}
      </main>

      <div className={`lnat-ws-toast ${toast ? 'show' : ''}`}>{toast}</div>
    </div>
  );
};

export default LnatWorkspace;
